import { useEffect } from "react";

export default function ReadMoreModal({
  title, subtitle, body, accentColor = "#8A1613",
  icon, isPhoto = false, extra, onClose,
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  return (
    /* Backdrop */
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        /* Backdrop itself does NOT scroll */
        overflow: "hidden",
      }}
    >
      {/* Modal panel */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 24,
          width: "100%",
          maxWidth: 520,
          /* Key: cap the height so it never taller than the viewport */
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",   /* header fixed, body scrolls */
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        {/* ── Fixed header strip — never scrolls ── */}
        <div style={{
          height: 120,
          flexShrink: 0,                /* never squish the header */
          background: `linear-gradient(135deg, ${accentColor}, rgba(149,187,131,0.6))`,
          display: "flex",
          alignItems: "flex-end",
          position: "relative",
          overflow: "hidden",
        }}>
          {isPhoto && icon && (
            <img src={icon} alt={title}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          )}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.62) 0%, transparent 65%)",
          }} />

          <div style={{
            position: "relative", zIndex: 1,
            padding: "1rem 1.5rem",
            display: "flex", alignItems: "center", gap: "0.75rem",
          }}>
            {!isPhoto && icon && (
              <div style={{
                width: 42, height: 42, borderRadius: 11,
                background: "rgba(255,255,255,0.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.3rem", flexShrink: 0,
              }}>{icon}</div>
            )}
            <div>
              <p style={{
                fontFamily: "var(--font)", fontWeight: 900,
                fontSize: "1.15rem", color: "#fff", margin: 0, lineHeight: 1.2,
              }}>{title}</p>
              {subtitle && (
                <p style={{
                  fontFamily: "var(--font)", fontWeight: 600,
                  fontSize: "0.68rem", color: "rgba(255,255,255,0.7)",
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  margin: "0.2rem 0 0",
                }}>{subtitle}</p>
              )}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute", top: 11, right: 11, zIndex: 2,
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(0,0,0,0.45)", border: "none",
              color: "#fff", fontSize: "0.9rem", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(138,22,19,0.85)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.45)"}
          >✕</button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{
          flex: 1,              /* fills remaining modal height */
          overflowY: "auto",    /* scrolls when content is tall */
          padding: "1.75rem 2rem 2rem",
          /* Nice scrollbar styling */
          scrollbarWidth: "thin",
          scrollbarColor: `${accentColor}55 transparent`,
        }}>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.97rem",
            color: "rgba(11,11,11,0.72)",
            lineHeight: 1.85,
            margin: 0,
            whiteSpace: "pre-wrap",   /* respects newlines in the text */
          }}>
            {body}
          </p>

          {extra && (
            <div style={{ marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid rgba(11,11,11,0.08)" }}>
              {extra}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
